import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { FiMap, FiLock, FiClock, FiChevronRight, FiCheck, FiX, FiUnlock, FiCopy } from "react-icons/fi";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import { getLatestExtensionRequest, PendingExtensionRequest } from "../../../actions/stay-actions";
import { openDoor } from "../../../actions/unit-actions";
import { HeroSection } from "../../../components/HeroSection";
import SnackbarComponent from "../../../components/Snackbar/Snackbar";
import { useSnack } from "../../../hooks/useSnack";
import "./HomeDark.scss";

// Eastern Time zone for Florida properties
const TIMEZONE = "America/New_York";

// Default cover image for properties
const DEFAULT_COVER_IMAGE = "https://cdn.pixabay.com/photo/2016/06/24/10/47/house-1477041_1280.jpg";

function HomeDark() {
  const navigate = useNavigate();
  const { user } = useContext<AuthContextType>(AuthContext);
  const snack = useSnack();
  const { t } = useTranslation();

  const [modalOpen, setModalOpen] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [pendingRequest, setPendingRequest] = useState<PendingExtensionRequest | null>(null);
  const [dismissedExtensionIds, setDismissedExtensionIds] = useState<string[]>(() => {
    const stored = localStorage.getItem("dismissedExtensionIds");
    return stored ? JSON.parse(stored) : [];
  });
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Determine if we're before the arrival date
  const today = moment().startOf('day');
  const arrivalDate = user?.checkIn ? moment(user.checkIn).startOf('day') : null;
  const isBeforeArrival = arrivalDate ? today.isBefore(arrivalDate) : false;

  const toggleModal = () => setModalOpen(!modalOpen);

  const handleCopy = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  // Fetch latest extension request (includes Pending, Approved, Rejected)
  useEffect(() => {
    const fetchLatestRequest = async () => {
      try {
        const response = await getLatestExtensionRequest();
        setPendingRequest(response.data);
      } catch (err) {
        console.error("Error fetching latest extension request:", err);
      }
    };

    fetchLatestRequest();
  }, []);

  const handleOpenDoor = async () => {
    setIsUnlocking(true);
    try {
      await openDoor();
      snack.setMessage(t("home.doorUnlocked"));
      snack.setSeverity("success");
      snack.onOpen();
    } catch (error: any) {
      snack.setMessage(error.response?.data?.message || t("home.doorUnlockFailed"));
      snack.setSeverity("error");
      snack.onOpen();
    } finally {
      setIsUnlocking(false);
      setModalOpen(false);
    }
  };

  // Format date with time in Eastern Time
  const formatDateTime = (dateStr: string) => {
    return moment.utc(dateStr).tz(TIMEZONE).format("MMM D, h:mm A");
  };

  const dismissExtensionCard = (requestId: string) => {
    const updated = [...dismissedExtensionIds, requestId];
    setDismissedExtensionIds(updated);
    localStorage.setItem("dismissedExtensionIds", JSON.stringify(updated));
  };

  const isExtensionDismissed = pendingRequest && dismissedExtensionIds.includes(pendingRequest.id);

  // Calculate nights
  const nights = user?.checkIn && user?.checkOut
    ? Math.ceil((new Date(user.checkOut).getTime() - new Date(user.checkIn).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Cover image - use unit photo or default
  const coverImage = user?.unitPhotoUrl || DEFAULT_COVER_IMAGE;

  return (
    <div className="home-dark dark-theme">
      {/* Hero Section with Property Image */}
      <HeroSection
        imageUrl={coverImage}
        title={user?.unitName || "Welcome"}
        subtitle={user?.partnerName}
        height="large"
      />

      {/* Main Content */}
      <div className="home-dark__content">
        {/* Welcome Message */}
        <div className="welcome-section">
          <p className="welcome-text">
            {t("home.welcomeGuest", { name: user?.name || t("profile.guest") })}
          </p>
          {nights > 0 && (
            <span className="nights-badge">
              {nights} {nights !== 1 ? t("common.nights") : t("common.night")}
            </span>
          )}
        </div>

        {/* Address & Directions */}
        {user?.unitAddress && (
          <div className="info-row">
            <span className="info-row__text">{user.unitAddress}</span>
            <a
              href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(user.unitAddress)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="info-row__action"
            >
              <FiMap size={22} />
            </a>
          </div>
        )}

        <div className="divider" />

        {/* WiFi Section */}
        {user?.wifiNetworkName && user?.wifiPassword && (
          <>
            <div className="info-columns">
              <div className="info-column">
                <span className="info-column__label">{t("home.wifiNetwork")}</span>
                <div className="info-column__row">
                  <span className="info-column__value">{user.wifiNetworkName}</span>
                  <button
                    className={`info-column__copy ${copiedField === 'network' ? 'info-column__copy--copied' : ''}`}
                    onClick={() => handleCopy(user.wifiNetworkName!, 'network')}
                    aria-label={t("common.copy")}
                  >
                    {copiedField === 'network' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </div>
              </div>
              <div className="info-column">
                <span className="info-column__label">{t("home.password")}</span>
                <div className="info-column__row">
                  <span className="info-column__value">{user.wifiPassword}</span>
                  <button
                    className={`info-column__copy ${copiedField === 'password' ? 'info-column__copy--copied' : ''}`}
                    onClick={() => handleCopy(user.wifiPassword!, 'password')}
                    aria-label={t("common.copy")}
                  >
                    {copiedField === 'password' ? <FiCheck size={14} /> : <FiCopy size={14} />}
                  </button>
                </div>
              </div>
            </div>
            <div className="divider" />
          </>
        )}

        {/* Check-out Section */}
        {user?.checkOut && (
          <>
            <div className="info-single">
              <span className="info-single__label">{t("home.checkOut")}</span>
              <span className="info-single__value">{moment.utc(user.checkOut).tz(TIMEZONE).format("MMM D, YYYY @ h:mm A")}</span>
            </div>
            <div className="divider" />
          </>
        )}

        {/* Door Access Warning (before arrival) */}
        {isBeforeArrival && user?.checkIn && (
          <div className="access-warning">
            <FiClock size={16} />
            <span>{t("home.doorAccessAvailable", { date: formatDateTime(user.checkIn) })}</span>
          </div>
        )}

        {/* Open Door Button */}
        <button
          className={`open-door-btn ${isUnlocking ? 'loading' : ''} ${isBeforeArrival ? 'disabled' : ''}`}
          onClick={toggleModal}
          disabled={isUnlocking || isBeforeArrival}
        >
          {isUnlocking ? (
            <>
              <span className="loading-spinner" />
              <span>{t("home.unlocking")}</span>
            </>
          ) : (
            <>
              <FiLock size={24} />
              <span>{t("home.openDoor")}</span>
            </>
          )}
        </button>

        {/* Extension Status Cards */}
        {pendingRequest && pendingRequest.status === "Approved" && !isExtensionDismissed && (
          <div className="status-card status-card--success" onClick={() => navigate("/admin/stay")}>
            <div className="status-card__icon">
              <FiCheck size={20} />
            </div>
            <div className="status-card__content">
              <h4>{t("home.extensionApproved")}</h4>
              <p>{t("home.extraNightsConfirmed", { count: pendingRequest.numberOfNights })}</p>
            </div>
            <button className="status-card__dismiss" onClick={(e) => { e.stopPropagation(); dismissExtensionCard(pendingRequest.id); }}>
              <FiX size={16} />
            </button>
            <FiChevronRight className="status-card__arrow" size={20} />
          </div>
        )}

        {pendingRequest && pendingRequest.status === "Rejected" && !isExtensionDismissed && (
          <div className="status-card status-card--error" onClick={() => navigate("/admin/stay")}>
            <div className="status-card__icon">
              <FiX size={20} />
            </div>
            <div className="status-card__content">
              <h4>{t("home.extensionDeclined")}</h4>
              <p>{t("home.requestNotApproved")}</p>
            </div>
            <button className="status-card__dismiss" onClick={(e) => { e.stopPropagation(); dismissExtensionCard(pendingRequest.id); }}>
              <FiX size={16} />
            </button>
            <FiChevronRight className="status-card__arrow" size={20} />
          </div>
        )}

        {pendingRequest && pendingRequest.status === "Pending" && (
          <div className="pending-banner" onClick={() => navigate("/admin/upgrades")}>
            <div className="pending-banner__icon">
              <FiClock size={20} />
            </div>
            <div className="pending-banner__content">
              <h4 className="pending-banner__title">{t("home.extensionPending")}</h4>
              <p className="pending-banner__text">
                {t("home.nightsRequested", { count: pendingRequest.numberOfNights })}
              </p>
            </div>
            <FiChevronRight className="pending-banner__arrow" size={20} />
          </div>
        )}

        {/* Timezone Note */}
        <div className="timezone-note">
          {t("common.easternTime")}
        </div>
      </div>

      {/* Door Unlock Confirmation Modal */}
      {modalOpen && (
        <div className="door-confirm-modal">
          <div className="door-confirm-modal__backdrop" onClick={toggleModal} />
          <div className="door-confirm-modal__content">
            <div className="door-confirm-modal__icon">
              <FiUnlock size={32} />
            </div>
            <h3 className="door-confirm-modal__title">{t("doorConfirm.title")}</h3>
            <p className="door-confirm-modal__text">
              {t("doorConfirm.message")}
            </p>
            <div className="door-confirm-modal__actions">
              <button
                className="door-confirm-modal__btn door-confirm-modal__btn--cancel"
                onClick={toggleModal}
                disabled={isUnlocking}
              >
                {t("common.cancel")}
              </button>
              <button
                className="door-confirm-modal__btn door-confirm-modal__btn--confirm"
                onClick={handleOpenDoor}
                disabled={isUnlocking}
              >
                {isUnlocking ? t("home.unlocking") : t("doorConfirm.yesUnlock")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar for notifications */}
      <SnackbarComponent
        isOpen={snack.isOpen}
        severity={snack.severity}
        onClose={snack.onClose}
        message={snack.message}
      />
    </div>
  );
}

export default HomeDark;
